import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { AREA_KINDS, AREA_KIND_LABELS, type AreaGeometry, type AreaKind, type TenantArea } from '@ww/shared';
import { api, type AreaMatch } from '../api';
import { errorMessage } from '../lib/status';
import { I, Icon } from '../lib/icons';
import WeatherMap, { type MapTenant } from './WeatherMap';
import { ErrorBox } from './ui';

export interface AreaValue {
  area: TenantArea | null;
  boundary: AreaGeometry | null;
  /** Suggested map centre for the picked area; the caller decides whether to adopt it. */
  center: [number, number] | null;
}

export interface AreaPickerProps {
  /** Two-letter USPS code; searches are scoped to it. */
  state: string;
  value: AreaValue;
  onChange: (v: AreaValue) => void;
  /** Base map context (existing boundary, timezone) — optional. */
  tenant?: MapTenant | null;
  /** Fixed height in px. Omit to fill the parent — used by the full-height coverage page. */
  mapHeight?: number;
}

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY: AreaValue = { area: null, boundary: null, center: null };

function ringArea(verts: [number, number][]): number {
  let sum = 0;
  for (let i = 0; i < verts.length; i += 1) {
    const [x1, y1] = verts[i]!;
    const [x2, y2] = verts[(i + 1) % verts.length]!;
    sum += x1 * y2 - x2 * y1;
  }
  return sum / 2;
}

/** GeoJSON wants exterior rings counter-clockwise; clicks can go either way. */
function toPolygon(verts: [number, number][]): AreaGeometry {
  const ring = ringArea(verts) < 0 ? [...verts].reverse() : verts;
  return { type: 'Polygon', coordinates: [[...ring, ring[0]!]] };
}

function centerOf(verts: [number, number][]): [number, number] {
  const xs = verts.map((v) => v[0]);
  const ys = verts.map((v) => v[1]);
  return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
}

export default function AreaPicker({ state, value, onChange, tenant, mapHeight }: AreaPickerProps) {
  const [kind, setKind] = useState<AreaKind>(value.area?.kind ?? 'county');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AreaMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verts, setVerts] = useState<[number, number][]>([]);
  const [customName, setCustomName] = useState(value.area?.kind === 'custom' ? value.area.label : '');
  const reqId = useRef(0);

  const st = state.trim().toUpperCase();
  const lookup = kind === 'custom' ? null : kind;

  // Search is scoped to a state, so an empty/short state code can't return anything useful.
  useEffect(() => {
    if (!lookup || st.length !== 2) {
      setResults(null);
      return;
    }
    const id = (reqId.current += 1);
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const r = await api.searchAreas(lookup, st, q.trim());
        if (reqId.current !== id) return;
        setResults(Array.isArray(r?.areas) ? r.areas : []);
        setError(null);
      } catch (err) {
        if (reqId.current !== id) return;
        setResults([]);
        setError(errorMessage(err));
      } finally {
        if (reqId.current === id) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [lookup, st, q]);

  const pick = useCallback(
    async (m: AreaMatch) => {
      setError(null);
      try {
        const d = await api.areaDetail(m.kind, m.geoid);
        onChange({
          area: { kind: m.kind, label: d.name, geoid: d.geoid },
          boundary: d.boundary,
          center: d.center,
        });
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [onChange],
  );

  const switchKind = (next: AreaKind) => {
    setKind(next);
    setError(null);
    setResults(null);
    setQ('');
    // Leaving a kind abandons its selection, so the preview never disagrees with the tab.
    if (next !== value.area?.kind) onChange(EMPTY);
    if (next !== 'custom') setVerts([]);
  };

  const addVertex = useCallback((lat: number, lon: number) => {
    setVerts((v) => [...v, [lon, lat]]);
  }, []);

  const applyCustom = () => {
    if (verts.length < 3) return;
    onChange({
      area: {
        kind: 'custom',
        label: customName.trim() || 'Custom area',
        geoid: null,
      },
      boundary: toPolygon(verts),
      center: centerOf(verts),
    });
  };

  // Re-editing a saved custom area starts from a clean canvas rather than
  // silently reusing stale vertices from a previous selection.
  const customApplied = value.area?.kind === 'custom' && !!value.boundary;
  const drawing = kind === 'custom' && !customApplied;

  const previewArea = useMemo(() => value.boundary ?? null, [value.boundary]);

  return (
    // The wrapper is the query container; the picker lays itself out from its OWN width,
    // not the viewport's, because two of the three call sites are narrow cards on a wide screen.
    <div
      className="area-picker-wrap"
      style={mapHeight ? ({ ['--area-h']: `${mapHeight}px` } as CSSProperties) : undefined}
    >
      <div className="area-picker">
        <div className="area-picker-controls">
          <div className="area-kinds" role="group" aria-label="Coverage area type">
            {AREA_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                className="chip hov"
                aria-pressed={kind === k}
                onClick={() => switchKind(k)}
              >
                {AREA_KIND_LABELS[k]}
              </button>
            ))}
          </div>

          <ErrorBox error={error} />

          {lookup && (
            <>
              <label className="field">
                <span className="lbl">
                  Search {lookup === 'county' ? 'counties' : 'cities and towns'} in {st || '—'}
                </span>
                <input
                  className="fld"
                  value={q}
                  placeholder={lookup === 'county' ? 'Pulaski' : 'Little Rock'}
                  disabled={st.length !== 2}
                  onChange={(e) => setQ(e.target.value)}
                />
              </label>
              {st.length !== 2 && (
                <div className="muted" style={{ fontSize: 13 }}>
                  Enter a state code first.
                </div>
              )}
              <div className="scroll area-results">
                {searching && (
                  <div className="muted" style={{ padding: '8px 10px' }}>
                    Searching…
                  </div>
                )}
                {!searching && results?.length === 0 && (
                  <div className="muted" style={{ padding: '8px 10px' }}>
                    No matches.
                  </div>
                )}
                {!searching &&
                  (results ?? []).map((m) => (
                    <button
                      key={m.geoid}
                      type="button"
                      className="area-result hov"
                      aria-pressed={value.area?.geoid === m.geoid}
                      onClick={() => void pick(m)}
                    >
                      <span>{m.name}</span>
                      <span className="mono muted" style={{ fontSize: 12 }}>
                        {m.geoid}
                      </span>
                    </button>
                  ))}
              </div>
            </>
          )}

          {kind === 'custom' && (
            <>
              <label className="field">
                <span className="lbl">Area name</span>
                <input
                  className="fld"
                  value={customName}
                  placeholder="Custom area"
                  onChange={(e) => setCustomName(e.target.value)}
                />
              </label>
              <div className="area-draw-bar">
                <span className="muted" style={{ flexGrow: 1, fontSize: 13 }}>
                  {customApplied
                    ? `Area set · ${value.area?.label}`
                    : `${verts.length} corner${verts.length === 1 ? '' : 's'} — click the map to add more`}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm hov"
                  disabled={!verts.length}
                  onClick={() => setVerts((v) => v.slice(0, -1))}
                >
                  Undo
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm hov"
                  disabled={!verts.length && !customApplied}
                  onClick={() => {
                    setVerts([]);
                    onChange(EMPTY);
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm hov"
                  disabled={verts.length < 3}
                  onClick={applyCustom}
                >
                  <Icon d={I.check} size={15} />
                  Use this area
                </button>
              </div>
            </>
          )}
        </div>

        {/* No pixel height: .area-map is position:relative and WeatherMap fills it. */}
        <div className="area-map">
          <WeatherMap
            tenant={tenant ?? null}
            alerts={[]}
            assets={[]}
            showRadar={false}
            showLegend={false}
            compact
            drawing={drawing}
            drawVertices={verts}
            onDrawVertex={addVertex}
            previewArea={previewArea}
            fitToGeometry={previewArea}
            placingHint="Click to add corners — at least three make an area"
          />
        </div>
      </div>
    </div>
  );
}
